import React, { useState, useEffect, useRef } from 'react';
import { X, Send, MessageCircle, Minimize2, Paperclip, Image as ImageIcon } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

export default function ChatBot() {
  const [isOpen, setIsOpen] = useState(false);
  const [isMinimized, setIsMinimized] = useState(false);
  const [messages, setMessages] = useState([]);
  const [inputMessage, setInputMessage] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const [attachedFiles, setAttachedFiles] = useState([]);
  const messagesEndRef = useRef(null);
  const fileInputRef = useRef(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  useEffect(() => {
    // Initialize with welcome message when chat opens
    if (isOpen && messages.length === 0) {
      setMessages([
        {
          role: 'assistant',
          content: 'Hi! I\'m Janet, your virtual assistant at Elite Roofing and Construction. 👋 How can I help you today?\n\nFeel free to describe your project and attach photos if you\'d like!',
          timestamp: new Date()
        }
      ]);
    }
  }, [isOpen]);

  const handleFileSelect = (e) => {
    const files = Array.from(e.target.files);
    
    // Validate file count
    if (attachedFiles.length + files.length > 5) {
      alert('You can only attach up to 5 photos at a time');
      return;
    }

    // Validate file types (images only)
    const validFiles = files.filter(file => {
      if (!file.type.startsWith('image/')) {
        alert(`${file.name} is not an image file`);
        return false;
      }
      return true;
    });

    setAttachedFiles(prev => [...prev, ...validFiles]);
  };

  const removeFile = (index) => {
    setAttachedFiles(prev => prev.filter((_, i) => i !== index));
  };

  const handleSendMessage = async (e) => {
    e.preventDefault();
    
    if ((!inputMessage.trim() && attachedFiles.length === 0) || isLoading) return;

    const userMessage = inputMessage.trim();
    const files = attachedFiles;
    setInputMessage('');
    setAttachedFiles([]);
    
    // Add user message to chat
    const userMsgContent = userMessage + (files.length > 0 ? `\n\n📎 ${files.length} photo(s) attached` : '');
    setMessages(prev => [
      ...prev,
      {
        role: 'user',
        content: userMsgContent,
        timestamp: new Date(),
        attachments: files
      }
    ]);

    setIsLoading(true);

    try {
      // Prepare form data for file upload
      const formData = new FormData();
      formData.append('message', userMessage || 'Attached photos');
      if (sessionId) {
        formData.append('session_id', sessionId);
      }
      
      // Append files
      files.forEach((file, index) => {
        formData.append(`file${index}`, file);
      });

      const response = await fetch(`${BACKEND_URL}/api/chat/message`, {
        method: 'POST',
        body: formData
      });

      if (!response.ok) {
        throw new Error('Failed to get response');
      }

      const data = await response.json();
      
      // Update session ID if it's a new session
      if (!sessionId) {
        setSessionId(data.session_id);
      }

      // Add assistant response to chat
      setMessages(prev => [
        ...prev,
        {
          role: 'assistant',
          content: data.message,
          timestamp: new Date(data.timestamp)
        }
      ]);
    } catch (error) {
      console.error('Chat error:', error);
      setMessages(prev => [
        ...prev,
        {
          role: 'assistant',
          content: 'Sorry, I\'m having trouble responding right now. Please try again or call us at (407) 785-0642.',
          timestamp: new Date()
        }
      ]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleClose = () => {
    setIsOpen(false);
    setIsMinimized(false);
  };

  const handleMinimize = () => {
    setIsMinimized(!isMinimized);
  };

  if (!isOpen) {
    return (
      <button
        onClick={() => setIsOpen(true)}
        className="fixed right-6 z-50 bg-gradient-to-r from-blue-900 to-blue-800 text-white rounded-full p-3 shadow-lg hover:shadow-xl transform hover:scale-105 transition-all duration-200 flex items-center gap-2"
        style={{ bottom: 'calc(4rem + 24px)' }} // 4rem (64px) for call bar + 24px spacing
        data-testid="chat-bubble-button"
        title="Chat with Janet"
      >
        <MessageCircle className="w-5 h-5" />
        <span className="font-semibold text-sm pr-1">Chat with Janet</span>
      </button>
    );
  }

  return (
    <div
      className={`fixed right-6 z-50 bg-white rounded-lg shadow-2xl transition-all duration-300 ${
        isMinimized ? 'w-80' : 'w-96'
      }`}
      style={{ 
        maxHeight: isMinimized ? '60px' : '600px', 
        height: isMinimized ? '60px' : '600px',
        bottom: 'calc(4rem + 24px)' // 4rem (64px) for call bar + 24px spacing
      }}
      data-testid="chat-window"
    >
      {/* Header */}
      <div className="bg-gradient-to-r from-blue-900 to-blue-800 text-white p-4 rounded-t-lg flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 bg-white rounded-full flex items-center justify-center text-blue-900 font-bold">
            J
          </div>
          <div>
            <div className="font-semibold">Janet</div>
            <div className="text-xs text-blue-100">Elite Roofing Assistant</div>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={handleMinimize}
            className="hover:bg-blue-700 rounded p-1 transition-colors"
            data-testid="minimize-chat-button"
          >
            <Minimize2 className="w-5 h-5" />
          </button>
          <button
            onClick={handleClose}
            className="hover:bg-blue-700 rounded p-1 transition-colors"
            data-testid="close-chat-button"
          >
            <X className="w-5 h-5" />
          </button>
        </div>
      </div>

      {!isMinimized && (
        <>
          {/* Messages */}
          <div className="flex-1 overflow-y-auto p-4 space-y-4 bg-gray-50" style={{ height: '460px' }}>
            {messages.map((message, index) => (
              <div
                key={index}
                className={`flex ${message.role === 'user' ? 'justify-end' : 'justify-start'}`}
              >
                <div
                  className={`max-w-[80%] rounded-lg p-3 ${
                    message.role === 'user'
                      ? 'bg-blue-900 text-white'
                      : 'bg-white text-gray-800 shadow-sm border border-gray-200'
                  }`}
                >
                  <div className="text-sm whitespace-pre-wrap">{message.content}</div>
                  {message.attachments && message.attachments.length > 0 && (
                    <div className="mt-2 flex flex-wrap gap-1">
                      {message.attachments.map((file, idx) => (
                        <div key={idx} className="text-xs bg-white/20 px-2 py-1 rounded">
                          📷 {file.name}
                        </div>
                      ))}
                    </div>
                  )}
                  <div className={`text-xs mt-1 ${message.role === 'user' ? 'text-blue-100' : 'text-gray-500'}`}>
                    {message.timestamp.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                  </div>
                </div>
              </div>
            ))}
            {isLoading && (
              <div className="flex justify-start">
                <div className="bg-white text-gray-800 shadow-sm border border-gray-200 rounded-lg p-3">
                  <div className="flex items-center gap-2">
                    <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '0ms' }}></div>
                    <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '150ms' }}></div>
                    <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '300ms' }}></div>
                  </div>
                </div>
              </div>
            )}
            <div ref={messagesEndRef} />
          </div>

          {/* File attachments preview */}
          {attachedFiles.length > 0 && (
            <div className="px-4 py-2 bg-gray-100 border-t border-gray-200">
              <div className="text-xs text-gray-600 mb-2">Attached photos ({attachedFiles.length}/5):</div>
              <div className="flex flex-wrap gap-2">
                {attachedFiles.map((file, index) => (
                  <div key={index} className="relative bg-white px-2 py-1 rounded text-xs flex items-center gap-1">
                    <ImageIcon className="w-3 h-3" />
                    <span className="max-w-[100px] truncate">{file.name}</span>
                    <button
                      onClick={() => removeFile(index)}
                      className="text-red-600 hover:text-red-800 ml-1"
                    >
                      ×
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Input */}
          <form onSubmit={handleSendMessage} className="p-4 border-t border-gray-200 bg-white rounded-b-lg">
            <div className="flex gap-2">
              <input
                type="file"
                ref={fileInputRef}
                onChange={handleFileSelect}
                accept="image/*"
                multiple
                className="hidden"
              />
              <button
                type="button"
                onClick={() => fileInputRef.current?.click()}
                disabled={isLoading || attachedFiles.length >= 5}
                className="p-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                title="Attach photos"
              >
                <Paperclip className="w-5 h-5 text-gray-600" />
              </button>
              <input
                type="text"
                value={inputMessage}
                onChange={(e) => setInputMessage(e.target.value)}
                placeholder="Type your message..."
                className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                disabled={isLoading}
                data-testid="chat-input"
              />
              <button
                type="submit"
                disabled={(!inputMessage.trim() && attachedFiles.length === 0) || isLoading}
                className="bg-red-600 text-white px-4 py-2 rounded-lg hover:bg-red-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                data-testid="send-message-button"
              >
                <Send className="w-5 h-5" />
              </button>
            </div>
            <div className="text-xs text-gray-500 mt-2 text-center">
              Questions about your project? I'm here to help! 🏠
            </div>
          </form>
        </>
      )}
    </div>
  );
}
